import type { FC } from 'react';

import { Calendar } from '../calendar';
import { DateInput } from '../date-input';
import { DatePickerBackdrop } from './date-picker-backdrop';
import { DatePickerButtonGroup } from './date-picker-button-group';
import { DatePickerClose } from './date-picker-close';
import { DatePickerContent } from './date-picker-content';
import { DatePickerFooter } from './date-picker-footer';
import { DatePickerInputGroup } from './date-picker-input-group';
import { DatePickerPortal } from './date-picker-portal';
import { DatePickerRoot } from './date-picker-root';
import { DatePickerTrigger } from './date-picker-trigger';
import type { DatePickerPropsInterface } from './types/date-picker.types';

/**
 * @since 5.0.0
 */

export const DatePicker: FC<DatePickerPropsInterface> & {
  Root: typeof DatePickerRoot;
  Trigger: typeof DatePickerTrigger;
  Content: typeof DatePickerContent;
  Portal: typeof DatePickerPortal;
  Backdrop: typeof DatePickerBackdrop;
  Footer: typeof DatePickerFooter;
  Close: typeof DatePickerClose;
  InputGroup: typeof DatePickerInputGroup;
  ButtonGroup: typeof DatePickerButtonGroup;
} = (props: DatePickerPropsInterface) => {
  const {
    calendar = <Calendar />,
    input = <DateInput />,
    css: cssProp,
    trigger,
    footer = <DatePickerButtonGroup />,
    closeModalOnDateSelect = false,
    portalId,
    portalRoot,
    portalPreserveTabOrder,
    ...rest
  } = props;

  return (
    <DatePicker.Root closeModalOnDateSelect={closeModalOnDateSelect} {...rest}>
      <DatePicker.InputGroup>
        {input}
        <DatePicker.Trigger>{trigger}</DatePicker.Trigger>
      </DatePicker.InputGroup>
      <DatePicker.Portal id={portalId} root={portalRoot} preserveTabOrder={portalPreserveTabOrder}>
        <DatePicker.Backdrop>
          <DatePicker.Content css={cssProp}>
            {calendar}
            {footer && !closeModalOnDateSelect ? (
              <DatePicker.Footer>{footer}</DatePicker.Footer>
            ) : null}
          </DatePicker.Content>
        </DatePicker.Backdrop>
      </DatePicker.Portal>
    </DatePicker.Root>
  );
};

DatePicker.displayName = 'DatePicker';

DatePicker.Root = DatePickerRoot;
DatePicker.Trigger = DatePickerTrigger;
DatePicker.Content = DatePickerContent;
DatePicker.Portal = DatePickerPortal;
DatePicker.Backdrop = DatePickerBackdrop;
DatePicker.Footer = DatePickerFooter;
DatePicker.Close = DatePickerClose;
DatePicker.InputGroup = DatePickerInputGroup;
DatePicker.ButtonGroup = DatePickerButtonGroup;
